import { makeStyles } from '@material-ui/styles';
import { useFieldArray, Control, Controller, useWatch } from 'react-hook-form';
import cn from 'clsx';

import {
    Theme,
    AddCircleLine,
    IconButton,
    IndeterminateCircle,
    Text,
} from '@bizzabo/design-system';

import { SchedulingPreferencesFormValues } from './useSchedulingPreferencesForm/types';
import TimeDropdown, { TIME_DROPDOWN_OPTIONS } from './TimeDropdown';
import useTypeTracking from '../../../analytics';

interface AvailabilitySpansPickerProps {
    nestIndex: number;
    control: Control<SchedulingPreferencesFormValues>;
}

const AvailabilitySpansPicker: React.FC<AvailabilitySpansPickerProps> = ({
    nestIndex,
    control,
}) => {
    const { fields, remove, append } = useFieldArray({
        control,
        name: `availability.${nestIndex}.intervals` as const,
    });

    const availability = useWatch({
        name: 'availability',
        control,
    });

    const { trackClickEvent } = useTypeTracking({
        data: { category: 'MEETINGS_SETTINGS' },
    });

    const addSpan = () => {
        if (fields.length === 0) {
            trackClickEvent({
                subCategory: 'ADD_AVAILABILITY_DAY',
            });
        }
        append({
            start: null,
            end: null,
        });
    };

    const removeSpan = (intervalIndex: number) => {
        if (fields.length === 1) {
            trackClickEvent({
                subCategory: 'REMOVE_AVAILABILITY_DAY',
            });
        }
        remove(intervalIndex);
    };

    const trackChangeTimeDropdown = () => {
        trackClickEvent({
            subCategory: 'EDIT_AVAILABILITY_HOURS',
        });
    };

    const isAddSpanShow = (intervalIndex: number) => {
        const start = availability[nestIndex].intervals[intervalIndex].start;
        const end = availability[nestIndex].intervals[intervalIndex].end;
        return intervalIndex === fields.length - 1 && start !== null && end !== null;
    };

    const classes = useStyles();
    return (
        <div className={classes.startEndRows}>
            {fields.map((item, intervalIndex) => {
                return (
                    <div key={item.id} className={classes.startEndRow}>
                        <div className={classes.inputs}>
                            <Controller
                                name={`availability.${nestIndex}.intervals.${intervalIndex}.start`}
                                control={control}
                                render={({ field: { onChange, value, name, ref } }) => (
                                    <TimeDropdown
                                        isFirstDay={nestIndex === 0}
                                        isLastDay={availability.length - 1 === nestIndex}
                                        item={availability[nestIndex]}
                                        control={control}
                                        valueTextAlign="left"
                                        inputRef={ref}
                                        onChange={option => {
                                            onChange(option.value);
                                            trackChangeTimeDropdown();
                                        }}
                                        name={name}
                                        value={TIME_DROPDOWN_OPTIONS.find(
                                            option => option.value === value
                                        )}
                                    />
                                )}
                            />
                            <Text variant="t81" className={classes.toText}>
                                To
                            </Text>
                            <Controller
                                name={`availability.${nestIndex}.intervals.${intervalIndex}.end`}
                                control={control}
                                render={({ field: { onChange, value, name, ref } }) => (
                                    <TimeDropdown
                                        isFirstDay={nestIndex === 0}
                                        isLastDay={availability.length - 1 === nestIndex}
                                        item={availability[nestIndex]}
                                        control={control}
                                        valueTextAlign="right"
                                        inputRef={ref}
                                        onChange={option => {
                                            onChange(option.value);
                                            trackChangeTimeDropdown();
                                        }}
                                        name={name}
                                        value={TIME_DROPDOWN_OPTIONS.find(
                                            option => option.value === value
                                        )}
                                    />
                                )}
                            />
                        </div>
                        <div className={classes.controls}>
                            <IconButton
                                className={classes.controlButton}
                                icon={IndeterminateCircle}
                                onClick={() => removeSpan(intervalIndex)}
                                variant="fillPrimary"
                            />
                            {isAddSpanShow(intervalIndex) && (
                                <IconButton
                                    className={classes.controlButton}
                                    icon={AddCircleLine}
                                    onClick={addSpan}
                                    variant="fillPrimary"
                                />
                            )}
                        </div>
                    </div>
                );
            })}
            {fields.length === 0 && (
                <IconButton
                    className={cn(classes.controlButton, classes.startEndRow)}
                    icon={AddCircleLine}
                    onClick={addSpan}
                    variant="fillPrimary"
                />
            )}
        </div>
    );
};

const useStyles = makeStyles<Theme>(
    theme => ({
        startEndRows: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        },
        startEndRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 16,
        },
        inputs: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            height: 36,
            borderRadius: 6,
            background: theme.customization.interactive.G20,
            color: theme.customization.interactive.G40,
            padding: '4px 8px',
        },
        toText: {
            margin: '0 1em',
            [theme.breakpoints.up('sm')]: {
                margin: '0 16px',
            },
        },
        controls: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginLeft: 4,
        },
        controlButton: {
            background: 'transparent',
            '&:hover': {
                background: 'transparent',
                color: theme.customization.interactive.G40,
            },
            color: theme.customization.interactive.G40,
            mixBlendMode: 'normal',
        },
    }),
    { classNamePrefix: 'people-user-settings-availability-spans' }
);

export default AvailabilitySpansPicker;
